import { type Action, createStore } from 'react-sweet-state';

import { GRAPHQL_ERROR_CODES } from 'src/data/graphqlErrorCodes';
import { queryOAuthConsentRequest } from 'src/graphql/oauthConsent';
import { getParsedGraphQLFirstError, type PlatformGraphQLError } from 'src/lib/graphql';
import { captureMessage } from 'src/lib/sentry';
import type { BackendOAuthConsentRequest } from 'src/types/backend';

import {
  getEligibleCustomers,
  getInitialWorkspaceSelection,
  getOnlyWorkspaceId,
  type OAuthConsentDecision,
} from '../../lib';

const DEFAULT_ERROR_MESSAGE =
  'This connection request could not be loaded. Start connecting again from your AI tool.';

export interface State {
  authorizeUrl: string;
  consentRequest: BackendOAuthConsentRequest | null;
  loading: boolean;
  errorMessage: string | null;
  selectedCustomerId: string | null;
  selectedWorkspaceId: string | null;
  pendingDecision: OAuthConsentDecision | null;
}

export interface ContainerProps {
  authorizeUrl: string;
}

const INITIAL_STATE: State = {
  authorizeUrl: '',
  consentRequest: null,
  loading: true,
  errorMessage: null,
  selectedCustomerId: null,
  selectedWorkspaceId: null,
  pendingDecision: null,
};

const isBackendRefusal = ({ code }: PlatformGraphQLError): boolean =>
  code !== GRAPHQL_ERROR_CODES.undefined && code !== GRAPHQL_ERROR_CODES.networkError;

const fetchConsentRequest =
  (): Action<State> =>
  async ({ getState, setState }) => {
    const { authorizeUrl } = getState();

    setState({ loading: true, errorMessage: null });
    try {
      const consentRequest = await queryOAuthConsentRequest({ authorizeUrl });
      if (!consentRequest) {
        throw new Error('The consent request is empty');
      }

      setState({ consentRequest, ...getInitialWorkspaceSelection(consentRequest) });
    } catch (error) {
      const parsedError = getParsedGraphQLFirstError(error as Error | Error[]);

      // A GraphQL error that carries a code is a backend refusal with a message for the user.
      if (parsedError && isBackendRefusal(parsedError)) {
        setState({ errorMessage: parsedError.message });
        return;
      }

      captureMessage('Failed to fetch the OAuth consent request', {
        level: 'error',
        extra: { error },
      });
      setState({ errorMessage: DEFAULT_ERROR_MESSAGE });
    } finally {
      setState({ loading: false });
    }
  };

export const PRIVATE_ACTIONS = {
  init:
    (authorizeUrl: string): Action<State> =>
    ({ setState, dispatch }) => {
      setState({ ...INITIAL_STATE, authorizeUrl });
      dispatch(fetchConsentRequest());
    },
};

export const ACTIONS = {
  selectCustomer:
    (customerId: string): Action<State> =>
    ({ getState, setState }) => {
      const customer = getEligibleCustomers(getState().consentRequest?.workspaces ?? []).find(
        (option) => option.id === customerId
      );

      setState({
        selectedCustomerId: customerId,
        selectedWorkspaceId: getOnlyWorkspaceId(customer?.eligibleWorkspaces ?? []),
      });
    },

  selectWorkspace:
    (workspaceId: string): Action<State> =>
    ({ setState }) => {
      setState({ selectedWorkspaceId: workspaceId });
    },

  setPendingDecision:
    (pendingDecision: OAuthConsentDecision | null): Action<State> =>
    ({ setState }) => {
      setState({ pendingDecision });
    },

  getAuthorizeUrl:
    (): Action<State, ContainerProps, State['authorizeUrl']> =>
    ({ getState }) =>
      getState().authorizeUrl,

  getSelectedWorkspaceId:
    (): Action<State, ContainerProps, State['selectedWorkspaceId']> =>
    ({ getState }) =>
      getState().selectedWorkspaceId,
};

export type Actions = typeof ACTIONS;

export const MainStore = createStore<State, Actions, ContainerProps>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'oauth-consent-request',
});
